/**
 * Workflow-gate approval card.
 *
 * Surfaces a `checkpoint` the orchestrator paused at (intent / bindings /
 * draft / validation / judge) with clear Approve / Edit / Redirect actions. The card
 * is more prominent than a standard tool-call row because the agent is
 * genuinely waiting on the epi — continuing requires a decision, not just
 * reading.
 */

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ForkRightRoundedIcon from '@mui/icons-material/ForkRightRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import {
  alpha,
  Box,
  Button,
  ButtonBase,
  Chip,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';

import { Markdown } from './Markdown';
import { useActiveDataset } from '../context/ActiveDatasetContext';
import { useKnowledgeBrowserStore } from '../state/knowledgeBrowserStore';
import {
  isOperationalGlobalRule,
  type EpiAppliedKnowledge,
  type EpiChatSendPayload,
  type EpiCheckpoint,
} from '../types/epi-chat-types';

const isConceptGroupEntry = (entry: EpiAppliedKnowledge): boolean => entry.kind === 'concept_group';

/**
 * Citation list rendered inside the checkpoint card. Each entry shows the
 * recalled knowledge title plus a kind/scope/source line so the epi can see
 * what the agent inherited — and from where — before approving the gate.
 *
 * The visual emphasis is intentional: when one of these entries originates
 * from a different session (the "memory bleed" case from epi-d63262928e3d),
 * the source pill exposes that provenance so the epi can decide whether
 * yesterday's definition still applies today.
 */
const AppliedKnowledgeStrip = ({ entries }: { entries: EpiAppliedKnowledge[] }) => {
  const openBrowser = useKnowledgeBrowserStore((s) => s.open);
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);
  // TODO: cleanup with concept_group_prevalence_preview ff
  const activeDataset = useActiveDataset();

  // Operational global rules (warehouse quirks, schema-introspection
  // conventions, agent-behavior constraints) are recalled by the agent on
  // every session but the epi has no decision to make about them — surfacing
  // them here implies they are inherited assumptions the epi could dismiss,
  // which is wrong. Filter them out so the strip stays focused on entries
  // the epi actually has agency over.
  const visibleEntries = entries.filter((entry) => !isOperationalGlobalRule(entry));
  if (visibleEntries.length === 0) return null;

  const handleEntryClick = (entry: EpiAppliedKnowledge) => {
    if (isConceptGroupEntry(entry)) {
      setOpenConceptGroupId(entry.id);
      return;
    }
    openBrowser(entry.id);
  };

  return (
    <Box
      data-testid="epi-checkpoint-applied-knowledge"
      sx={{
        marginBottom: 1.5,
        padding: 1,
        borderRadius: 0.75,
        border: (theme) => `1px dashed ${theme.palette.divider}`,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ marginBottom: 0.5 }}>
        <HistoryRoundedIcon fontSize="small" color="secondary" />
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontWeight: 600,
          }}
        >
          Knowledge applied ({visibleEntries.length})
        </Typography>
      </Stack>
      <Stack direction="column" spacing={0.5}>
        {visibleEntries.map((entry) => (
          <Tooltip
            key={entry.id}
            placement="right-start"
            enterDelay={800}
            title={
              <Box>
                <Typography variant="caption" display="block" fontWeight={600}>
                  {entry.title}
                </Typography>
                <Typography variant="caption" color="inherit" display="block">
                  id: <code>{entry.id}</code>
                </Typography>
                <Typography variant="caption" color="inherit" display="block">
                  kind: {entry.kind} · scope: {entry.scope}
                </Typography>
                {entry.source && (
                  <Typography variant="caption" color="inherit" display="block">
                    source: {entry.source}
                  </Typography>
                )}
              </Box>
            }
          >
            <ButtonBase
              onClick={() => handleEntryClick(entry)}
              data-testid={`epi-checkpoint-applied-knowledge-entry-${entry.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                paddingX: 0.5,
                paddingY: 0.25,
                width: '100%',
                borderRadius: 0.5,
                textAlign: 'left',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
            >
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {entry.title}
              </Typography>
              <Chip
                size="small"
                label={entry.kind}
                variant="outlined"
                sx={{ height: 20, fontSize: (theme) => theme.typography.caption.fontSize }}
              />
              <Chip
                size="small"
                label={entry.scope}
                variant="outlined"
                sx={{ height: 20, fontSize: (theme) => theme.typography.caption.fontSize }}
              />
            </ButtonBase>
          </Tooltip>
        ))}
      </Stack>
      {openConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={openConceptGroupId}
          datasetReleaseId={activeDataset?.id ?? null}
          datasetReleaseDisplayName={activeDataset?.displayName ?? null}
          onClose={() => setOpenConceptGroupId(null)}
        />
      )}
    </Box>
  );
};

interface CheckpointCardProps {
  checkpoint: EpiCheckpoint;
  onAnswer: (answer: EpiChatSendPayload) => void;
  disabled?: boolean;
}

const STAGE_LABEL: Record<EpiCheckpoint['stage'], string> = {
  intent: 'Intent',
  bindings: 'Concept bindings',
  draft: 'Draft',
  validation: 'Verification',
  judge: 'Logic review',
  cohort_plan: 'Cohort plan',
  concept_group: 'New concept group',
  process_analysis: 'Process analysis',
};

const STAGE_PROMPT: Record<EpiCheckpoint['stage'], string> = {
  intent: 'Review the parsed study intent before the agent resolves concept groups.',
  bindings: 'Confirm the curated and proposed code sets before the agent drafts SQL.',
  draft: 'Review the draft envelope before verification runs.',
  validation: 'Resolve verification findings before re-drafting.',
  judge: 'Resolve the logic-review findings before re-drafting.',
  cohort_plan:
    'Review the cohort plan — variables, roles, and descriptions — before the agent starts building.',
  concept_group: 'No existing library group fits — confirm before the agent creates a new one.',
  process_analysis: 'Process the analysis to generate its results.',
};

type Mode = 'choose' | 'edit' | 'redirect';

export const CheckpointCard = ({ checkpoint, onAnswer, disabled }: CheckpointCardProps) => {
  const [mode, setMode] = useState<Mode>('choose');
  const [text, setText] = useState('');

  if (checkpoint.answered) {
    return (
      <Paper
        variant="outlined"
        sx={{
          padding: 1.5,
          backgroundColor: (theme) => theme.palette.action.hover,
          borderRadius: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ marginBottom: 0.5 }}>
          <Chip
            size="small"
            label={`Checkpoint · ${STAGE_LABEL[checkpoint.stage]}`}
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary">
            resolved
          </Typography>
        </Stack>
        <Typography variant="body2" fontWeight={500}>
          {checkpoint.answerText}
        </Typography>
      </Paper>
    );
  }

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onAnswer({ text: trimmed, checkpointId: checkpoint.checkpointId });
  };

  const handleApprove = () => submit('Approved — proceed with the proposed next step.');

  return (
    <Paper
      variant="outlined"
      sx={{
        padding: 1.5,
        borderRadius: 1,
        borderColor: (theme) => theme.palette.primary.main,
        borderLeftWidth: 4,
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ marginBottom: 1 }}>
        <Chip
          size="small"
          color="primary"
          label={`Checkpoint · ${STAGE_LABEL[checkpoint.stage]}`}
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="caption" color="text.secondary">
          Awaiting your review
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
        {STAGE_PROMPT[checkpoint.stage]}
      </Typography>

      {checkpoint.summary && (
        <Box sx={{ marginBottom: 1.25 }}>
          <Markdown>{checkpoint.summary}</Markdown>
        </Box>
      )}

      {checkpoint.appliedKnowledge && checkpoint.appliedKnowledge.length > 0 && (
        <AppliedKnowledgeStrip entries={checkpoint.appliedKnowledge} />
      )}

      {checkpoint.proposedNext && (
        <Box sx={{ marginBottom: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 600,
              marginBottom: 0.25,
            }}
          >
            Proposed next step
          </Typography>
          <Box
            sx={{
              padding: 1,
              borderRadius: 0.75,
              backgroundColor: (theme) => theme.palette.background.default,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              fontFamily: 'Menlo, Consolas, monospace',
              fontSize: (theme) => theme.typography.body2.fontSize,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {checkpoint.proposedNext}
          </Box>
        </Box>
      )}

      {mode === 'choose' && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleApprove}
            disabled={disabled}
          >
            Approve & continue
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditRoundedIcon />}
            onClick={() => setMode('edit')}
            disabled={disabled}
          >
            Edit before approving
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<ForkRightRoundedIcon />}
            onClick={() => setMode('redirect')}
            disabled={disabled}
          >
            Redirect
          </Button>
        </Stack>
      )}

      {(mode === 'edit' || mode === 'redirect') && (
        <Box sx={{ marginTop: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontWeight: 600,
              marginBottom: 0.5,
            }}
          >
            {mode === 'edit'
              ? 'Describe the edits to apply, then approve:'
              : 'Tell the agent what to do instead:'}
          </Typography>
          <TextField
            fullWidth
            autoFocus
            multiline
            minRows={2}
            size="small"
            placeholder={
              mode === 'edit'
                ? 'e.g. Change the window to 180 days pre-index and include urgent care.'
                : 'e.g. Stop — instead, build a cohort of adults newly initiating biologics.'
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit(text);
              }
            }}
            disabled={disabled}
          />
          <Stack direction="row" spacing={1} sx={{ marginTop: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => submit(text)}
              disabled={disabled || !text.trim()}
            >
              Send
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setMode('choose');
                setText('');
              }}
              disabled={disabled}
            >
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};
